import { useNavigate } from 'react-router';
import { PATHS } from '../../navigation';
import { t } from '../../theme';
import { useStore } from '../../store';
import { WeatherIcon } from './WeatherIcon';

/**
 * Stale after roughly two poll intervals.
 *
 * The panel keeps showing the last good forecast when the network drops —
 * that is the point of caching it — but a forecast from this morning must not
 * masquerade as live, so past this age the tile states when it was fetched.
 */
const STALE_AFTER_MS = 45 * 60_000;

function clockOf(iso: string): string {
  return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

export function WeatherWidget() {
  const weather = useStore((s) => s.weather);
  const navigate = useNavigate();

  const shell = (children: React.ReactNode, onClick?: () => void) => (
    <div
      className={onClick ? 'press' : undefined}
      onClick={onClick}
      style={{
        borderRadius: 28,
        background: t.surfaceRaised,
        boxShadow: 'inset 0 1px 0 oklch(1 0 0 / 0.07), 0 18px 34px -26px oklch(0.05 0 0 / 0.9)',
        padding: '18px 22px 16px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      }}
    >
      {children}
    </div>
  );

  // Not configured yet — say what to do about it rather than showing an empty box.
  if (!weather?.configured || !weather.report) {
    return shell(
      <>
        <div style={{ fontFamily: t.mono, fontSize: 13, letterSpacing: '0.16em', color: t.inkFaint }}>WETTER</div>
        <div style={{ marginTop: 10, fontSize: 20, fontWeight: 500, color: t.inkMuted, textWrap: 'pretty' }}>
          Kein Ort gewählt
        </div>
        <div style={{ marginTop: 4, fontSize: 16, color: t.inkFaint }}>Unter System einrichten</div>
      </>,
      () => navigate(PATHS.settings),
    );
  }

  const { report, fetchedAt, lastError } = weather;
  const age = fetchedAt ? Date.now() - new Date(fetchedAt).getTime() : Infinity;
  const stale = age > STALE_AFTER_MS || Boolean(lastError);

  return shell(
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <div
          style={{
            fontFamily: t.mono,
            fontSize: 13,
            letterSpacing: '0.16em',
            color: t.inkFaint,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {report.place.toUpperCase()}
        </div>
        {stale && fetchedAt && (
          <div style={{ fontFamily: t.mono, fontSize: 12, color: 'oklch(0.62 0.09 70)', whiteSpace: 'nowrap' }}>
            Stand {clockOf(fetchedAt)}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginTop: 10 }}>
        <div style={{ color: t.accent, display: 'flex', flex: 'none' }}>
          <WeatherIcon condition={report.condition} isDay={report.isDay} size={58} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 52, fontWeight: 500, letterSpacing: '-0.045em', lineHeight: 1 }}>
            {report.tempC}°
          </div>
          {/* Wraps rather than truncating: "Überwiegend klar" cut to
              "Überwiegend kl…" tells you less than a second line costs. */}
          <div style={{ marginTop: 4, fontSize: 17, color: t.inkMuted, textWrap: 'pretty', lineHeight: 1.25 }}>
            {report.conditionLabel}
          </div>
          {/* Only when it genuinely differs — a degree or two apart is noise,
              and it would push the condition onto two lines for nothing. */}
          {Math.abs(report.feelsLikeC - report.tempC) >= 3 && (
            <div style={{ marginTop: 2, fontSize: 15, color: t.inkFaint }}>
              gefühlt {report.feelsLikeC}°
            </div>
          )}
        </div>
      </div>

      {/* The line that earns the tile its space — only rendered when there is
          actually a change coming. */}
      {report.outlook && (
        <div style={{ marginTop: 12, fontSize: 19, fontWeight: 500, color: t.accent, textWrap: 'pretty' }}>
          {report.outlook}
        </div>
      )}

      <div style={{ flex: 1, minHeight: 12 }} />

      <div style={{ display: 'flex', gap: 4, justifyContent: 'space-between' }}>
        {report.hours.slice(0, 7).map((h) => (
          <div key={h.time} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, minWidth: 0 }}>
            <div style={{ fontFamily: t.mono, fontSize: 11, letterSpacing: '0.06em', color: t.inkFaint }}>
              {h.time.slice(0, 2)}
            </div>
            <div style={{ color: t.inkMuted, display: 'flex' }}>
              <WeatherIcon condition={h.condition} isDay size={20} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 500 }}>{h.tempC}°</div>
            <div
              style={{
                fontFamily: t.mono,
                fontSize: 11,
                // Below 20% the number is noise; leave the slot blank so the
                // eye only lands on hours that matter.
                color: h.precipChance >= 20 ? 'oklch(0.72 0.1 230)' : 'transparent',
              }}
            >
              {h.precipChance}%
            </div>
          </div>
        ))}
      </div>
    </>,
  );
}
